import React, { useState, useEffect } from "react";
import moment from "moment";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import Header from "../../../Common/Layout/Header";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import FlightLoader from "../../../Common/Layout/FlightLoader";
import ModifyFlightSearch1 from "../ModifyFlightSearch/ModifyFlightSearch1";

// Import your new hook
import { useFlightSearch } from "../../hooks/useFlightSearch";

import "./Flights1.css";

const Flights1 = () => {
  const { user, loading, currencies } = useAuth();
  const params = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  
  const [currency, setCurrency] = useState(user?.currency || "INR");
  const [searchQuery, setSearchQuery] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFares, setSelectedFares] = useState({});
  const [showFareModal, setShowFareModal] = useState(false);
  const[selectedFlightForModal, setSelectedFlightForModal] = useState(null);
  const[showDetailModal, setShowDetailModal] = useState(false);
  const [selectedFlightForDetail, setSelectedFlightForDetail] = useState(null);
  const [debug, setDebug] = useState({});

  // 🚀 FETCH FLIGHTS USING OUR NEW HOOK
  const { 
    data: flightData =[], 
    isLoading: pageLoading 
  } = useFlightSearch(searchQuery);

  // Filter states
  const[filters, setFilters] = useState({
    stops:[],
    departureTime: "",
    priceRange: 50000,
    airlines:[],
  });

  // Determine journey type from searchQuery
  const journeyType = searchQuery?.journeyType || "ONEWAY";
  const isRoundTrip = journeyType === "RETURN";

  // Get unique airlines from flight data
  const getAirlines = () => {
    const set = new Set();
    flightData.forEach((tab) => {
      if (tab?.dcFlightLegs && Array.isArray(tab.dcFlightLegs)) {
        tab.dcFlightLegs.forEach((flight) => {
          const airline = flight?.dcDisplayLegs?.[0]?.airlineData?.airline;
          if (airline) set.add(airline);
        });
      }
    });
    return Array.from(set);
  };

  const handleFilterChange = (type, value) => {
    setFilters((prev) => {
      if (type === "stops") {
        const updated = prev.stops.includes(value)
          ? prev.stops.filter((v) => v !== value)
          : [...prev.stops, value];
        return { ...prev, stops: updated };
      } else if (type === "airlines") {
        const updated = prev.airlines.includes(value)
          ? prev.airlines.filter((v) => v !== value)
          : [...prev.airlines, value];
        return { ...prev, airlines: updated };
      } else if (type === "departureTime") {
        return { ...prev, departureTime: value };
      } else if (type === "priceRange") {
        return { ...prev, priceRange: parseInt(value) };
      }
      return prev;
    });
  };

  const handleClearFilters = () => {
    setFilters({ stops:[], departureTime: "", priceRange: 50000, airlines:[] });
  };

  const applyFilters = (flights) => {
    if (!flights || !Array.isArray(flights)) return[];
    
    return flights.filter((flight) => {
      const displayLeg = flight?.dcDisplayLegs?.[0];
      if (!displayLeg) return false;
      
      const airline = displayLeg?.airlineData?.airline;
      const stops = displayLeg?.stops;
      const price = flight.totalPrice || 0;
      
      let departureHour = 0;
      if (displayLeg?.startSegment?.departureDateTime) {
        try {
          departureHour = new Date(displayLeg.startSegment.departureDateTime).getHours();
        } catch (e) {
          console.warn("Invalid date format:", displayLeg.startSegment.departureDateTime);
        }
      }

      const stopMatch = filters.stops.length === 0 || 
                       (stops !== undefined && stops !== null && filters.stops.includes(stops));
      const airlineMatch = filters.airlines.length === 0 || 
                          (airline && filters.airlines.includes(airline));
      const priceMatch = price <= filters.priceRange;
      
      let timeMatch = true;
      if (filters.departureTime) {
        if (filters.departureTime === "morning") timeMatch = departureHour >= 5 && departureHour < 12;
        else if (filters.departureTime === "afternoon") timeMatch = departureHour >= 12 && departureHour < 17;
        else if (filters.departureTime === "evening") timeMatch = departureHour >= 17 && departureHour < 21;
        else if (filters.departureTime === "night") timeMatch = departureHour >= 21 || departureHour < 5;
      }

      return stopMatch && airlineMatch && priceMatch && timeMatch;
    });
  };

  const getTimeFromMins = (mins) => {
    if (!mins && mins !== 0) return "0h 0m";
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h}h ${m}m`;
  };

  const handleSelectFare = (journeyType, flightLeg, fare, isOutbound = true) => {
    setSelectedFares((prev) => {
      if (!isRoundTrip) return { [journeyType]: { flightLeg, fare } };
      
      const newSelection = { ...prev };
      const routeKey = isOutbound ? "outbound" : "return";
      newSelection[routeKey] = {
        flightLeg,
        fare,
        journeyType: journeyType,
        route: isOutbound ? "outbound" : "return",
        locationFrom: flightLeg?.dcDisplayLegs?.[0]?.startSegment?.airportCode,
        locationTo: flightLeg?.dcDisplayLegs?.[0]?.endSegment?.airportCode,
      };
      return newSelection;
    });
  };

  const isFlightSelected = (journeyType, supplierLegId, isOutbound = true) => {
    if (!isRoundTrip) {
      return selectedFares[journeyType]?.flightLeg?.supplierLegId === supplierLegId;
    } else {
      const routeKey = isOutbound ? "outbound" : "return";
      return selectedFares[routeKey]?.flightLeg?.supplierLegId === supplierLegId;
    }
  };

  const getMinPrice = (flightLeg) => {
    if (!flightLeg?.dcPriceDetails?.length) return 0;
    return Math.min(...flightLeg.dcPriceDetails.map((fare) => fare.totalPrice || Infinity));
  };

  const openFareModal = (flight) => {
    setSelectedFlightForModal(flight);
    setShowFareModal(true);
  };

  const openDetailModal = (flight) => {
    setSelectedFlightForDetail(flight);
    setShowDetailModal(true);
  };

  const handleBookNow = (journeyType, flightLeg, isOutbound = true) => {
    const cheapestFare =[...flightLeg.dcPriceDetails].sort((a, b) => a.totalPrice - b.totalPrice)[0];
    handleSelectFare(journeyType, flightLeg, cheapestFare, isOutbound);
    
    if (!isRoundTrip) {
      const payload =[{ flightLeg: flightLeg, fare: cheapestFare, journeyType: journeyType }];
      navigate(
        "/template/flight-details/" + params.adults + "/" + params.kids + "/" + params.infants + "/" + params.isDomestic,
        { state: { searchQuery: searchQuery, payload: payload, journeyType: journeyType } }
      );
    } else {
      const routeKey = isOutbound ? "outbound" : "return";
      const otherRouteKey = isOutbound ? "return" : "outbound";
      
      if (selectedFares[otherRouteKey]) {
        const payload = [
          {
            flightLeg: isOutbound ? flightLeg : selectedFares[otherRouteKey].flightLeg,
            fare: isOutbound ? cheapestFare : selectedFares[otherRouteKey].fare,
            journeyType: journeyType,
            route: "outbound",
          },
          {
            flightLeg: isOutbound ? selectedFares[otherRouteKey].flightLeg : flightLeg,
            fare: isOutbound ? selectedFares[otherRouteKey].fare : cheapestFare,
            journeyType: journeyType,
            route: "return",
          }
        ];
        navigate(
          "/template/flight-details/" + params.adults + "/" + params.kids + "/" + params.infants + "/" + params.isDomestic,
          { state: { searchQuery: searchQuery, payload: payload, journeyType: journeyType } }
        );
      } else {
        alert(`Please select your ${isOutbound ? 'return' : 'outbound'} flight as well`);
      }
    }
  };

  const handleViewFaresAndSelect = (journeyType, flightLeg, fare) => {
    const isOutbound = activeTab === 0;
    handleSelectFare(journeyType, flightLeg, fare, isOutbound);
    
    if (!isRoundTrip) {
      const payload =[{ flightLeg: flightLeg, fare: fare, journeyType: journeyType }];
      navigate(
        "/template/flight-details/" + params.adults + "/" + params.kids + "/" + params.infants + "/" + params.isDomestic,
        { state: { searchQuery: searchQuery, payload: payload, journeyType: journeyType } }
      );
    } else {
      const otherRouteKey = isOutbound ? "return" : "outbound";
      if (selectedFares[otherRouteKey]) {
        const payload =[
          {
            flightLeg: isOutbound ? flightLeg : selectedFares[otherRouteKey].flightLeg,
            fare: isOutbound ? fare : selectedFares[otherRouteKey].fare,
            journeyType: journeyType,
            route: "outbound",
          },
          {
            flightLeg: isOutbound ? selectedFares[otherRouteKey].flightLeg : flightLeg,
            fare: isOutbound ? selectedFares[otherRouteKey].fare : fare,
            journeyType: journeyType,
            route: "return",
          }
        ];
        navigate(
          "/template/flight-details/" + params.adults + "/" + params.kids + "/" + params.infants + "/" + params.isDomestic,
          { state: { searchQuery: searchQuery, payload: payload, journeyType: journeyType } }
        );
      } else {
        alert(`Please select your ${isOutbound ? 'return' : 'outbound'} flight as well`);
        setShowFareModal(false);
      }
    }
  };

  const handleModifySearch = (newSearchQuery) => {
    setSearchQuery(newSearchQuery); // Updating this will automatically trigger useFlightSearch
    setSelectedFares({}); 
    setActiveTab(0);
  };

  useEffect(() => {
    if (!state?.searchQuery) {
      navigate("/template/flight/search");
    } else {
      setSearchQuery(state.searchQuery);
    }
  },[state, navigate]);

  if (loading) return <p>Loading...</p>;
  if (!user) return null;

  return (
    <div id="layout-wrapper">
      <Header User={user} activeSection="flights" />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="sf1-main-container">
              {/* Modify Search Component */}
              <ModifyFlightSearch1 
                onSearch={handleModifySearch} 
                initialSearchQuery={searchQuery}
              />

              {/* Flight Layout */}
              {pageLoading ? (
                <div className="row">
                  <div className="col-12">
                    <div className="text-center">
                      <div className="branded-loader-wrapper">
                        <FlightLoader />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="sf1-flight-layout">
                  {/* Filter Sidebar */}
                  <div className="sf1-filter-sidebar">
                    <div className="sf1-filter-header">
                      <h5><i className="fas fa-sliders-h me-2"></i>Filters</h5>
                      <a href="#" id="sf1-clearFilters" onClick={(e) => { e.preventDefault(); handleClearFilters(); }}>Clear All</a>
                    </div>

                    {/* Stops Filter */}
                    <div className="sf1-filter-group">
                      <p>Stops</p>
                      {[0, 1, 2].map((stop) => (
                        <div className="sf1-filter-checkbox" key={stop}>
                          <input
                            type="checkbox"
                            id={`sf1-stop${stop}`}
                            value={stop}
                            checked={filters.stops.includes(stop)}
                            onChange={() => handleFilterChange("stops", stop)}
                          />
                          <label htmlFor={`sf1-stop${stop}`}>
                            {stop === 0 ? "Non-stop" : `${stop} Stop${stop > 1 ? 's' : ''}`}
                          </label>
                        </div>
                      ))}
                    </div>

                    {/* Departure Time */}
                    <div className="sf1-filter-group">
                      <p>Departure Time</p>
                      {[
                        { value: "morning", label: "Morning (5am - 12pm)" },
                        { value: "afternoon", label: "Afternoon (12pm - 5pm)" },
                        { value: "evening", label: "Evening (5pm - 9pm)" },
                        { value: "night", label: "Night (9pm - 5am)" }
                      ].map((time) => (
                        <div className="sf1-filter-checkbox" key={time.value}>
                          <input
                            type="radio"
                            name="sf1-departureTime"
                            id={`sf1-${time.value}`}
                            value={time.value}
                            checked={filters.departureTime === time.value}
                            onChange={() => handleFilterChange("departureTime", time.value)}
                          />
                          <label htmlFor={`sf1-${time.value}`}>{time.label}</label>
                        </div>
                      ))}
                    </div>

                    {/* Price Range */}
                    <div className="sf1-filter-group">
                      <p>Price Range (Up to ₹{filters.priceRange})</p>
                      <input
                        type="range"
                        className="sf1-range-slider"
                        id="sf1-priceRange"
                        min="0"
                        max="100000"
                        step="1000"
                        value={filters.priceRange}
                        onChange={(e) => handleFilterChange("priceRange", parseInt(e.target.value))}
                      />
                      <div className="sf1-range-values">
                        <span>₹0</span>
                        <span>Up to ₹<span id="sf1-maxPrice">{filters.priceRange}</span></span>
                      </div>
                    </div>

                    {/* Airlines */}
                    <div className="sf1-filter-group">
                      <p>Airlines</p>
                      <div id="sf1-airlineFilters">
                        {getAirlines().map((airline) => (
                          <div className="sf1-filter-checkbox" key={airline}>
                            <input
                              type="checkbox"
                              className="sf1-airline-filter"
                              value={airline}
                              id={`sf1-airline-${airline}`}
                              checked={filters.airlines.includes(airline)}
                              onChange={() => handleFilterChange("airlines", airline)}
                            />
                            <label htmlFor={`sf1-airline-${airline}`}>{airline}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Results Area */}
                  <div className="sf1-results-area">
                    {/* Flight Count with Route Tabs */}
                    <div className="sf1-flight-count-card">
                      <div style={{ display: "flex", alignItems: "center", gap: "20px", flexWrap: "wrap" }}>
                        <span className="sf1-flight-count-badge">
                          <i className="fas fa-plane"></i>{" "}
                          <span id="sf1-flightCountNumber">
                            {flightData && flightData.length > 0 
                              ? (flightData[activeTab]?.dcFlightLegs?.length || 0) 
                              : 0}
                          </span> flights available
                        </span>
                        {flightData && flightData.length > 0 && (
                          <div className="sf1-route-tabs-vertical">
                            {flightData.map((item, index) => (
                              <button
                                key={index}
                                className={`sf1-route-tab-btn ${activeTab === index ? "active" : ""}`}
                                id={`sf1-tab${index}`}
                                onClick={() => setActiveTab(index)}
                              >
                                {item.locationFrom || "From"} → {item.locationTo || "To"}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="sf1-sort-info">
                        <i className="fas fa-sort-amount-down"></i> sorted by best
                      </span>
                    </div>

                    {/* Flight Results Container */}
                    <div id="sf1-flightResultsContainer" className="sf1-flight-list-container">
                      {flightData && flightData.length > 0 ? (
                        flightData.map((destination, destIndex) => {
                          const isOutbound = destIndex === 0;
                          const filteredFlights = applyFilters(destination?.dcFlightLegs ||[]);
                          
                          return (
                            <div
                              key={destIndex}
                              className={`sf1-route-section ${activeTab === destIndex ? "active" : ""}`}
                              id={`sf1-section${destIndex}`}
                            >
                              {filteredFlights.length > 0 ? (
                                <div className="sf1-flight-list">
                                  {filteredFlights.map((flightLeg, idx) => {
                                    const displayLeg = flightLeg?.dcDisplayLegs?.[0];
                                    if (!displayLeg) return null;
                                    
                                    const airlineData = displayLeg?.airlineData;
                                    const minPrice = getMinPrice(flightLeg);
                                    const isSelected = isRoundTrip 
                                      ? isFlightSelected(destination?.journeyType, flightLeg?.supplierLegId, isOutbound)
                                      : isFlightSelected(destination?.journeyType, flightLeg?.supplierLegId);
                                    const fareIdentifier = flightLeg?.dcPriceDetails?.[0]?.fareIdentifier || "PUBLISHED";

                                    return (
                                      <div className={`sf1-flight-card ${isSelected ? "sf1-selected" : ""}`} key={idx}>
                                        {/* Airline Section */}
                                        <div className="sf1-airline-section">
                                          <div className="sf1-airline-logo">
                                            <img 
                                              src={`/assets/images/airlinelogos/${airlineData?.airlineCode || 'default'}.png`}
                                              alt={airlineData?.airline}
                                              style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                              onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                                e.target.parentNode.innerHTML = '<i className="fas fa-building"></i>';
                                              }}
                                            />
                                          </div>
                                          <div className="sf1-airline-info">
                                            <h3>{airlineData?.flightNumber || "N/A"}</h3>
                                            <p>{airlineData?.airline || "Unknown Airline"}</p>
                                          </div>
                                        </div>

                                        {/* Route Section */}
                                        <div className="sf1-route-section-compact">
                                          <div className="sf1-from-point">
                                            <div className="sf1-code">{displayLeg?.startSegment?.airportCode || "---"}</div>
                                            <div className="sf1-time">
                                              {displayLeg?.startSegment?.departureDateTime 
                                                ? moment(displayLeg.startSegment.departureDateTime).format("HH:mm") 
                                                : "--:--"}
                                            </div>
                                            <div className="sf1-date">
                                              {displayLeg?.startSegment?.departureDateTime 
                                                ? moment(displayLeg.startSegment.departureDateTime).format("DD MMM YY") 
                                                : "---"}
                                            </div>
                                          </div>

                                          <div className="sf1-duration-section">
                                            <div className="sf1-duration">{getTimeFromMins(displayLeg?.totalDuration)}</div>
                                            <div className="sf1-flight-line">
                                              <span className="sf1-dots">.............</span>
                                            </div>
                                            <span className="sf1-stop-badge">
                                              {displayLeg?.stops === 0 ? "Non Stop" : `${displayLeg?.stops || 0} Stop${displayLeg?.stops > 1 ? 's' : ''}`}
                                            </span>
                                          </div>

                                          <div className="sf1-to-point">
                                            <div className="sf1-code">{displayLeg?.endSegment?.airportCode || "---"}</div>
                                            <div className="sf1-time">
                                              {displayLeg?.endSegment?.arrivalDateTime 
                                                ? moment(displayLeg.endSegment.arrivalDateTime).format("HH:mm") 
                                                : "--:--"}
                                            </div>
                                            <div className="sf1-date">
                                              {displayLeg?.endSegment?.arrivalDateTime 
                                                ? moment(displayLeg.endSegment.arrivalDateTime).format("DD MMM YY") 
                                                : "---"}
                                            </div>
                                          </div>
                                        </div>

                                        {/* Price & Action - Vertical Stack */}
                                        <div className="sf1-price-action-vertical">
                                          <div className="sf1-baggage-item">
                                            <i className="fas fa-suitcase"></i>
                                            <span>{flightLeg?.dcPriceDetails?.[0]?.cabinBaggage || "7 Kg"}</span>
                                          </div>
                                          <div className="sf1-baggage-item">
                                            <i className="fas fa-briefcase"></i>
                                            <span>{flightLeg?.dcPriceDetails?.[0]?.checkinBag || "20 Kg"}</span>
                                          </div>
                                          <div className="sf1-baggage-item">
                                            <i className="fas fa-chair"></i> {selectedFlightForDetail?.noOfSeats|| "5"} seats
                                          </div>
                                           <button
                                            className="sf1-view-fares-btn"
                                            onClick={() => openFareModal(flightLeg)}
                                          >
                                            View Fares
                                          </button>
                                          <button
                                            className="sf1-flight-details-btn"
                                            onClick={() => openDetailModal(flightLeg)}
                                          >
                                            Flight Details
                                          </button>
                                        </div>

                                        {/* Baggage Section with Flight Details below */}
                                        <div className="sf1-baggage-section">
                                          <span className="sf1-fare-type">{fareIdentifier}</span>
                                          <div className="sf1-price">
                                            <span className="sf1-currency">{currency}</span>
                                            <span className="sf1-amount">{Math.round(minPrice).toLocaleString()}</span>
                                          </div>
                                         
                                          <button
                                            className="sf1-book-now-btn"
                                            onClick={() => handleBookNow(destination?.journeyType || "ONEWAY", flightLeg, isOutbound)}
                                          >
                                            {isRoundTrip ? (isOutbound ? "SELECT OUTBOUND" : "SELECT RETURN") : "BOOK NOW"}
                                          </button>
                                          
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="sf1-no-flights-message">
                                  <p>No flights match your filters. Try adjusting your search criteria.</p>
                                </div>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="sf1-no-flights-message">
                          <p>No flights available. Please try a different search.</p>
                        </div>
                      )}
                    </div>

                    {/* Selected Flights Summary for Round Trip */}
                    {isRoundTrip && Object.keys(selectedFares).length > 0 && (
                      <div className="sf1-selected-flights-summary">
                        <h5>Selected Flights</h5>
                        {selectedFares.outbound && (
                          <div className="sf1-selected-flight-item">
                            <span>Outbound: {selectedFares.outbound.locationFrom || "---"} → {selectedFares.outbound.locationTo || "---"}</span>
                            <span className="sf1-selected-price">{currency} {Math.round(selectedFares.outbound.fare?.totalPrice || 0).toLocaleString()}</span>
                          </div>
                        )}
                        {selectedFares.return && (
                          <div className="sf1-selected-flight-item">
                            <span>Return: {selectedFares.return.locationFrom || "---"} → {selectedFares.return.locationTo || "---"}</span>
                            <span className="sf1-selected-price">{currency} {Math.round(selectedFares.return.fare?.totalPrice || 0).toLocaleString()}</span>
                          </div>
                        )}
                        {selectedFares.outbound && selectedFares.return && (
                          <div className="sf1-selected-total">
                            <strong>Total: {currency} {Math.round(
                              (selectedFares.outbound.fare?.totalPrice || 0) + 
                              (selectedFares.return.fare?.totalPrice || 0)
                            ).toLocaleString()}</strong>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* View Fares Modal */}
      {showFareModal && selectedFlightForModal && (
        <div className="sf1-modal-overlay" onClick={() => setShowFareModal(false)}>
          <div className="sf1-modal-content sf1-modal-xl" onClick={(e) => e.stopPropagation()}>
            <div className="sf1-modal-header">
              <h5 className="sf1-modal-title">
                Select Fare Type - {selectedFlightForModal?.dcDisplayLegs?.[0]?.airlineData?.flightNumber || "N/A"} · {selectedFlightForModal?.dcDisplayLegs?.[0]?.airlineData?.airline || "Unknown"}
              </h5>
              <button className="sf1-modal-close" onClick={() => setShowFareModal(false)}>&times;</button>
            </div>
            <div className="sf1-modal-body">
              <div className="sf1-fare-options-list">
                {selectedFlightForModal?.dcPriceDetails?.map((fare, index) => {
                  const currentJourneyType = flightData[activeTab]?.journeyType || "ONEWAY";
                  const isOutbound = activeTab === 0;
                  const routeKey = isRoundTrip ? (isOutbound ? "outbound" : "return") : currentJourneyType;
                  const isSelected = selectedFares[routeKey]?.fare?.supplierPriceId === fare?.supplierPriceId;
                  
                  return (
                    <div
                      key={index}
                      className={`sf1-fare-option ${isSelected ? "selected" : ""}`}
                      onClick={() => handleViewFaresAndSelect(currentJourneyType, selectedFlightForModal, fare)}
                    >
                      <div className="sf1-fare-radio">
                        <input
                          type="radio"
                          name="sf1-fareOption"
                          checked={isSelected}
                          readOnly
                        />
                      </div>
                      <div className="sf1-fare-details">
                        <span className="sf1-fare-price">{currency} {Math.round(fare?.totalPrice || 0).toLocaleString()}</span>
                        <span className="sf1-fare-name">{fare?.fareIdentifier || "PUBLISHED"}</span>
                        <span className="sf1-fare-refund">
                          <i className={`fas fa-${fare?.dcAdults?.refundable === "Refundable" ? "check-circle" : "times-circle"}`}></i>
                          {fare?.dcAdults?.refundable === "Refundable" ? " REFUNDABLE" : " NON-REFUNDABLE"}
                        </span>
                        <span className="sf1-fare-meal">
                          <i className="fas fa-utensils"></i>
                          {fare?.dcAdults?.mealType === "Free Meal" ? " FREE MEAL" : " PAID MEAL"}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="sf1-modal-footer">
              <button className="sf1-modal-btn sf1-modal-btn-secondary" onClick={() => setShowFareModal(false)}>Cancel</button>
              <button className="sf1-modal-btn sf1-modal-btn-primary" onClick={() => setShowFareModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Flight Details Modal - For single flight details */}
      {showDetailModal && selectedFlightForDetail && (
        <div className="sf1-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="sf1-modal-content sf1-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="sf1-modal-header">
              <h5 className="sf1-modal-title">
                Flight Details - {selectedFlightForDetail?.dcDisplayLegs?.[0]?.airlineData?.flightNumber || "N/A"} · {selectedFlightForDetail?.dcDisplayLegs?.[0]?.airlineData?.airline || "Unknown"}
              </h5>
              <button className="sf1-modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
            </div>
            <div className="sf1-modal-body">
              {selectedFlightForDetail?.dcSegments?.map((segment, idx) => {
                const displayLeg = selectedFlightForDetail?.dcDisplayLegs?.[0];
                return (
                  <div className="sf1-flight-segment" key={idx}>
                    <div className="sf1-segment-header">
                      <div className="sf1-segment-airline">
                        <img
                          src={`/assets/images/airlinelogos/${segment?.tFAirlineData?.airlineCode || 'default'}.png`}
                          alt={segment?.tFAirlineData?.airlineCode}
                          style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.parentNode.innerHTML = '<i className="fas fa-building"></i>';
                          }}
                        />
                        <div>
                          <h6 className="mb-0 fw-bold">{segment?.tFAirlineData?.airline || "Unknown"}</h6>
                          <small className="text-muted">
                            {segment?.tFAirlineData?.flightNumber || "N/A"} · {segment?.tFAirlineData?.equipmentType || "A380"}
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="sf1-segment-timeline">
                      <div className="text-center">
                        <div className="fw-bold fs-5">
                          {segment?.tFDepartureData?.departureDateTime 
                            ? moment(segment.tFDepartureData.departureDateTime).format("HH:mm") 
                            : "--:--"}
                        </div>
                        <span className="sf1-badge sf1-badge-light">
                          {segment?.tFDepartureData?.airportCode || "---"}
                        </span>
                        <div className="small mt-1">{segment?.tFDepartureData?.city || "Unknown"}</div>
                        {segment?.tFDepartureData?.terminal && (
                          <small className="text-muted">
                            Terminal {segment.tFDepartureData.terminal}
                          </small>
                        )}
                      </div>

                      <div className="text-center">
                        <div className="small fw-bold mb-1">
                          {getTimeFromMins(segment?.tFAirlineData?.duration)}
                        </div>
                        <i className="fas fa-plane text-primary"></i>
                        <div className="small text-muted mt-1">
                          {displayLeg?.stops === 0 ? "Non Stop" : `${displayLeg?.stops || 0} Stop${displayLeg?.stops > 1 ? 's' : ''}`}
                        </div>
                      </div>

                      <div className="text-center">
                        <div className="fw-bold fs-5">
                          {segment?.tFArrivalData?.arrivalDateTime 
                            ? moment(segment.tFArrivalData.arrivalDateTime).format("HH:mm") 
                            : "--:--"}
                        </div>
                        <span className="sf1-badge sf1-badge-light">
                          {segment?.tFArrivalData?.airportCode || "---"}
                        </span>
                        <div className="small mt-1">{segment?.tFArrivalData?.city || "Unknown"}</div>
                        {segment?.tFArrivalData?.terminal && (
                          <small className="text-muted">
                            Terminal {segment.tFArrivalData.terminal}
                          </small>
                        )}
                      </div>
                    </div>

                    {idx < selectedFlightForDetail.dcSegments.length - 1 && (
                      <div className="sf1-layover-info">
                        <i className="fas fa-clock"></i>
                        Layover in {selectedFlightForDetail.dcSegments[idx]?.tFArrivalData?.city || "Unknown"}: {getTimeFromMins(selectedFlightForDetail.dcSegments[idx + 1]?.tFAirlineData?.connectingTime || 90)}
                      </div>
                    )}
                  </div>
                );
              })}

              <div className="sf1-flight-detail-footer">
                <div className="row">
                  <div className="col-4">
                    <i className="fas fa-suitcase"></i>
                    <div><strong>{selectedFlightForDetail?.dcPriceDetails?.[0]?.cabinBaggage || "7 Kg"}</strong></div>
                    <small>Cabin</small>
                  </div>
                  <div className="col-4">
                    <i className="fas fa-briefcase"></i>
                    <div><strong>{selectedFlightForDetail?.dcPriceDetails?.[0]?.checkinBag || "20 Kg"}</strong></div>
                    <small>Check-in</small>
                  </div>
                  <div className="col-4">
                    <i className="fas fa-chair"></i>
                    <div><strong>{selectedFlightForDetail?.noOfSeats || "5"} seats</strong></div>
                    <small>Available</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Flights1;